/**
 * /tracing-lightbox — everything that is arithmetic and can be proved in node
 * (tests/js/tracing-lightbox-*.test.mjs). No DOM here: the page (index.js)
 * and the painter (render.js) call these.
 *
 *  1. Screen calibration. The value is SHARED with /online-ruler: both tools
 *     read and write `__toolSettings('screen-calibration', 'pxPerMm' | 'dpr')`,
 *     i.e. localStorage `ts_screen-calibration_pxPerMm`. A person calibrates
 *     once. The stored number is CSS px per millimetre at the stored
 *     devicePixelRatio; browser zoom changes the CSS px, so the effective
 *     value is rescaled by storedDpr / currentDpr (physics: device pixels per
 *     mm do not change).
 *  2. Real size and tiling. A picture larger than the screen is cut into
 *     screen-sized tiles that overlap by `overlap` mm; registration crosses sit
 *     in the overlap strips so the same cross shows on both neighbours.
 *  3. Image processing on plain typed arrays: tone LUT, grayscale, Otsu,
 *     threshold, Canny-style outline with hysteresis and speck removal,
 *     dilation for thick lines.
 *  4. The print resolution stored in a JPEG (JFIF / EXIF) or PNG (pHYs) — a
 *     scanned pattern can be shown at the size it was scanned at.
 *  5. Gesture math: pinch (move + zoom + twist), zoom around a point, fit.
 */

/* ── 1. calibration ─────────────────────────────────────────────────────── */

export const CARD_W_MM = 85.6;     // ISO/IEC 7810 ID-1 — any bank card
export const CARD_H_MM = 53.98;
export const MM_PER_IN = 25.4;
export const MIN_PXMM = 1.6;       // same bounds as /online-ruler
export const MAX_PXMM = 9.0;
export const DEFAULT_PXMM = 96 / 25.4;
export const CAL_TOOL = 'screen-calibration';

export function clampPxPerMm(v) {
    return Math.max(MIN_PXMM, Math.min(MAX_PXMM, v));
}

export function validPxPerMm(v) {
    return typeof v === 'number' && isFinite(v) && v >= MIN_PXMM && v <= MAX_PXMM;
}

/**
 * The number to draw with now. `stored` was measured at `storedDpr`; the page
 * now runs at `dprNow`. Returns null when nothing valid is stored.
 */
export function effectivePxPerMm(stored, storedDpr, dprNow) {
    if (!validPxPerMm(stored)) return null;
    const a = Number(storedDpr), b = Number(dprNow);
    if (!(a > 0) || !(b > 0) || Math.abs(a - b) < 0.01) return stored;
    return clampPxPerMm(stored * a / b);
}

/** The on-screen card is `cardWidthPx` CSS px wide when it matches a real card. */
export function calibFromCard(cardWidthPx) {
    const w = Number(cardWidthPx);
    if (!(w > 0)) return null;
    return clampPxPerMm(w / CARD_W_MM);
}

/** A bar `barPx` CSS px long measured `measuredMm` with a ruler. */
export function calibFromBar(barPx, measuredMm) {
    const px = Number(barPx), mm = Number(measuredMm);
    if (!(px > 0) || !(mm > 0)) return null;
    const v = px / mm;
    if (v < MIN_PXMM * 0.5 || v > MAX_PXMM * 2) return null;   // a typo, not a screen
    return clampPxPerMm(v);
}

/* ── units ─────────────────────────────────────────────────────────────── */

export function toMm(v, unit) {
    const n = Number(v);
    if (!isFinite(n)) return NaN;
    if (unit === 'in') return n * MM_PER_IN;
    if (unit === 'mm') return n;
    return n * 10;                                  // cm
}

export function fromMm(mm, unit) {
    if (unit === 'in') return mm / MM_PER_IN;
    if (unit === 'mm') return mm;
    return mm / 10;
}

/** A length for a label: 1 decimal in cm, 2 in inches, trailing zeros dropped. */
export function fmtLen(mm, unit, dec) {
    const v = fromMm(mm, unit);
    const d = unit === 'in' ? 2 : 1;
    let s = (Math.round(v * Math.pow(10, d)) / Math.pow(10, d)).toFixed(d);
    s = s.replace(/\.?0+$/, '');
    return dec && dec !== '.' ? s.replace('.', dec) : s;
}

/* ── 2. geometry: rotation, paper, tiling ─────────────────────────────── */

export const PAPERS = {
    a5: [148, 210], a4: [210, 297], a3: [297, 420], a2: [420, 594],
    a1: [594, 841], a0: [841, 1189], letter: [215.9, 279.4], tabloid: [279.4, 431.8],
};

/** Axis-aligned box of a w×h rectangle turned by `deg`. */
export function rotatedBox(w, h, deg) {
    const r = (deg || 0) * Math.PI / 180;
    let c = Math.abs(Math.cos(r)), s = Math.abs(Math.sin(r));
    // kill float dust at the right angles, or 90° gives a 1e-14 sliver
    if (c < 1e-12) c = 0;
    if (s < 1e-12) s = 0;
    return { w: w * c + h * s, h: w * s + h * c };
}

/**
 * The picture WIDTH in mm (its own width, before the turn) that makes the
 * turned picture as large as it can be on the named paper with `marginMm`
 * on every side. The sheet is used in whichever orientation fits better.
 */
export function widthForPaper(picW, picH, angle, paperKey, marginMm) {
    const p = PAPERS[paperKey];
    if (!p || !(picW > 0) || !(picH > 0)) return null;
    const m = Math.max(0, marginMm || 0);
    const unit = rotatedBox(1, picH / picW, angle);          // box of a 1-mm-wide picture
    let best = 0;
    for (const [pw, ph] of [[p[0], p[1]], [p[1], p[0]]]) {
        const aw = pw - 2 * m, ah = ph - 2 * m;
        if (aw <= 0 || ah <= 0) continue;
        best = Math.max(best, Math.min(aw / unit.w, ah / unit.h));
    }
    return best > 0 ? best : null;
}

/** Width in mm of a picture `srcW` px wide printed at `dpi`. */
export function widthForDpi(srcW, dpi) {
    if (!(srcW > 0) || !(dpi > 0)) return null;
    return srcW / dpi * MM_PER_IN;
}

function axisTiles(content, tile, overlap) {
    if (content <= tile + 1e-6) return { n: 1, step: tile - overlap, total: tile };
    const step = tile - overlap;
    const n = 1 + Math.ceil((content - tile) / step - 1e-9);
    return { n, step, total: step * (n - 1) + tile };
}

/**
 * Cut a `contentW × contentH` (mm) picture into `tileW × tileH` (mm) screens
 * that overlap by `overlap` mm. Tiles are numbered row by row, left to right.
 * The picture sits centred on the tile grid, so the spare room is split
 * between both ends instead of leaving the last screen nearly empty.
 */
export function tileLayout({ contentW, contentH, tileW, tileH, overlap }) {
    if (!(tileW > 0) || !(tileH > 0)) throw new Error('tile size');
    const cw = Math.max(0, contentW || 0), ch = Math.max(0, contentH || 0);
    // an overlap that eats most of a screen would never finish; keep a useful step
    const ov = Math.max(0, Math.min(overlap || 0, Math.min(tileW, tileH) * 0.45));
    const X = axisTiles(cw, tileW, ov), Y = axisTiles(ch, tileH, ov);
    const tiles = [];
    for (let r = 0; r < Y.n; r++) {
        for (let c = 0; c < X.n; c++) {
            tiles.push({ index: tiles.length, row: r, col: c, x: c * X.step, y: r * Y.step, w: tileW, h: tileH });
        }
    }
    return {
        cols: X.n, rows: Y.n, count: X.n * Y.n,
        stepX: X.step, stepY: Y.step, tileW, tileH, overlap: ov,
        gridW: X.total, gridH: Y.total,
        contentW: cw, contentH: ch,
        offsetX: (X.total - cw) / 2, offsetY: (Y.total - ch) / 2,
        tiles,
    };
}

/** «3 / 6 · row 1, column 3» — 1-based numbers for people. */
export function tileLabel(L, index) {
    const t = L.tiles[index];
    if (!t) return null;
    return { n: index + 1, total: L.count, row: t.row + 1, col: t.col + 1 };
}

/** How many screens the same picture takes with the screen turned 90°. */
export function orientationAdvice(contentW, contentH, screenWmm, screenHmm, overlap) {
    const now = tileLayout({ contentW, contentH, tileW: screenWmm, tileH: screenHmm, overlap }).count;
    const turned = tileLayout({ contentW, contentH, tileW: screenHmm, tileH: screenWmm, overlap }).count;
    return { now, turned, better: turned < now };
}

/**
 * Registration crosses, in grid millimetres. Each sits in the MIDDLE of an
 * overlap strip, so it is inside both tiles that share the strip: trace it on
 * one sheet, line it up with the pencil mark on the next. Along every shared
 * edge there are two crosses (a single cross fixes the shift but not the
 * turn), plus one where four tiles meet.
 */
export function registrationMarks(L) {
    const marks = [];
    const seen = new Set();
    const add = (x, y) => {
        const k = Math.round(x * 100) + ':' + Math.round(y * 100);
        if (seen.has(k)) return;
        seen.add(k);
        marks.push({ x, y });
    };
    const half = L.overlap / 2;
    for (let c = 1; c < L.cols; c++) {                       // vertical seams
        const x = c * L.stepX + half;
        for (let r = 0; r < L.rows; r++) {
            const y0 = r * L.stepY;
            add(x, y0 + L.tileH * 0.25);
            add(x, y0 + L.tileH * 0.75);
        }
    }
    for (let r = 1; r < L.rows; r++) {                       // horizontal seams
        const y = r * L.stepY + half;
        for (let c = 0; c < L.cols; c++) {
            const x0 = c * L.stepX;
            add(x0 + L.tileW * 0.25, y);
            add(x0 + L.tileW * 0.75, y);
        }
    }
    for (let c = 1; c < L.cols; c++) {
        for (let r = 1; r < L.rows; r++) add(c * L.stepX + half, r * L.stepY + half);
    }
    return marks;
}

/** The crosses that fall on tile `index`, in that tile's own mm (0,0 = its top-left). */
export function marksInTile(L, marks, index, pad) {
    const t = L.tiles[index];
    if (!t) return [];
    const p = pad || 0;
    return marks
        .filter((m) => m.x >= t.x - p && m.x <= t.x + t.w + p && m.y >= t.y - p && m.y <= t.y + t.h + p)
        .map((m) => ({ x: m.x - t.x, y: m.y - t.y }));
}

/**
 * The strips of tile `index` that are repeated on a neighbour, in the tile's
 * own mm: [{x, y, w, h, side}]. The page tints them so a person sees which band
 * will come back on the next screen.
 */
export function overlapStrips(L, index) {
    const t = L.tiles[index];
    if (!t || L.overlap <= 0) return [];
    const o = L.overlap, out = [];
    if (t.col > 0) out.push({ side: 'left', x: 0, y: 0, w: o, h: t.h });
    if (t.col < L.cols - 1) out.push({ side: 'right', x: t.w - o, y: 0, w: o, h: t.h });
    if (t.row > 0) out.push({ side: 'top', x: 0, y: 0, w: t.w, h: o });
    if (t.row < L.rows - 1) out.push({ side: 'bottom', x: 0, y: t.h - o, w: t.w, h: o });
    return out;
}

/** Which tile a point in grid mm falls on (the first one, row by row). */
export function tileAt(L, x, y) {
    for (const t of L.tiles) {
        if (x >= t.x && x <= t.x + t.w && y >= t.y && y <= t.y + t.h) return t.index;
    }
    return -1;
}

/* ── 5. gesture math ──────────────────────────────────────────────────── */

export function normAngle(a) {
    let x = ((a + 180) % 360 + 360) % 360 - 180;
    if (x === -180) x = 180;
    return x;
}

/** Snap to the nearest multiple of 90° when within `tol` degrees. */
export function snapAngle(a, tol) {
    const n = normAngle(a);
    const q = Math.round(n / 90) * 90;
    return Math.abs(n - q) <= (tol == null ? 3 : tol) ? normAngle(q) : n;
}

/**
 * Two fingers moved from (a0, b0) to (a1, b1). The picture follows them as a
 * rigid similarity: it shifts, scales by the change in finger distance and
 * turns by the change in their angle, around the fingers — a point under a
 * finger stays under it. t = {x, y, s, a} (centre in screen px, px per source
 * px, degrees).
 */
export function pinchUpdate(t0, a0, b0, a1, b1, opts) {
    const o = opts || {};
    const v0x = b0.x - a0.x, v0y = b0.y - a0.y, v1x = b1.x - a1.x, v1y = b1.y - a1.y;
    const l0 = Math.hypot(v0x, v0y), l1 = Math.hypot(v1x, v1y);
    if (l0 < 1e-6 || l1 < 1e-6) return { ...t0 };
    const k = o.scale === false ? 1 : l1 / l0;
    const d = o.rotate === false ? 0 : Math.atan2(v1y, v1x) - Math.atan2(v0y, v0x);
    const cos = Math.cos(d), sin = Math.sin(d);
    const px = t0.x - a0.x, py = t0.y - a0.y;
    return {
        x: a1.x + k * (cos * px - sin * py),
        y: a1.y + k * (sin * px + cos * py),
        s: t0.s * k,
        a: normAngle(t0.a + d * 180 / Math.PI),
    };
}

/** Zoom by `f` keeping the screen point (px, py) fixed. */
export function zoomAt(t, px, py, f) {
    return { x: px + (t.x - px) * f, y: py + (t.y - py) * f, s: t.s * f, a: t.a };
}

/** Centre the picture and scale it so its turned box fills `fill` of the screen. */
export function fitTransform(srcW, srcH, angle, screenW, screenH, fill) {
    const f = fill == null ? 0.94 : fill;
    const b = rotatedBox(srcW, srcH, angle);
    const s = Math.min(screenW * f / b.w, screenH * f / b.h);
    return { x: screenW / 2, y: screenH / 2, s, a: normAngle(angle || 0) };
}

/** Cover mode: the turned picture covers the whole screen. */
export function fillTransform(srcW, srcH, angle, screenW, screenH) {
    const b = rotatedBox(srcW, srcH, angle);
    const s = Math.max(screenW / b.w, screenH / b.h);
    return { x: screenW / 2, y: screenH / 2, s, a: normAngle(angle || 0) };
}

/* ── 3. image processing on typed arrays ──────────────────────────────── */

/**
 * Tone curve as a 256-entry table. contrast and brightness are −100…100,
 * the classic contrast factor (259·(C+255))/(255·(259−C)); invert last.
 */
export function makeLut(opts) {
    const o = opts || {};
    const c = Math.max(-100, Math.min(100, o.contrast || 0)) * 2.55;
    const b = Math.max(-100, Math.min(100, o.brightness || 0)) * 2.55;
    const f = (259 * (c + 255)) / (255 * (259 - c));
    const lut = new Uint8Array(256);
    for (let i = 0; i < 256; i++) {
        let v = f * (i - 128) + 128 + b;
        v = v < 0 ? 0 : v > 255 ? 255 : v;
        if (o.invert) v = 255 - v;
        lut[i] = Math.round(v);
    }
    return lut;
}

export function isIdentityLook(o) {
    return !o.gray && !o.invert && !(o.contrast) && !(o.brightness);
}

/** Luminance 0…255 of each pixel, transparent parts read as white paper. */
export function toGray(rgba, n) {
    const g = new Uint8ClampedArray(n);
    for (let i = 0, j = 0; i < n; i++, j += 4) {
        const a = rgba[j + 3] / 255;
        const l = 0.299 * rgba[j] + 0.587 * rgba[j + 1] + 0.114 * rgba[j + 2];
        g[i] = l * a + 255 * (1 - a);
    }
    return g;
}

/** Photo mode, in place: optional grayscale, then the tone table on every channel. */
export function adjustRgba(rgba, n, look) {
    const lut = makeLut(look);
    const gray = !!look.gray;
    for (let i = 0, j = 0; i < n; i++, j += 4) {
        if (gray) {
            const l = Math.round(0.299 * rgba[j] + 0.587 * rgba[j + 1] + 0.114 * rgba[j + 2]);
            const v = lut[l];
            rgba[j] = v; rgba[j + 1] = v; rgba[j + 2] = v;
        } else {
            rgba[j] = lut[rgba[j]]; rgba[j + 1] = lut[rgba[j + 1]]; rgba[j + 2] = lut[rgba[j + 2]];
        }
    }
    return rgba;
}

/** Otsu's threshold of a 0…255 image: the level that best splits it in two. */
export function otsu(gray) {
    const hist = new Float64Array(256);
    for (let i = 0; i < gray.length; i++) hist[gray[i]]++;
    const total = gray.length;
    let sum = 0;
    for (let i = 0; i < 256; i++) sum += i * hist[i];
    let sumB = 0, wB = 0, best = 0, level = 128;
    for (let t = 0; t < 256; t++) {
        wB += hist[t];
        if (!wB) continue;
        const wF = total - wB;
        if (!wF) break;
        sumB += t * hist[t];
        const mB = sumB / wB, mF = (sum - sumB) / wF;
        const between = wB * wF * (mB - mF) * (mB - mF);
        if (between > best) { best = between; level = t; }
    }
    // pixels <= level are the dark class; the mask takes gray < level + 1
    return level + 1;
}

/** Dark pixels (gray < level) become ink: 255 in the mask. */
export function thresholdMask(gray, level) {
    const m = new Uint8Array(gray.length);
    for (let i = 0; i < gray.length; i++) m[i] = gray[i] < level ? 255 : 0;
    return m;
}

function boxH(a, b, w, h, r) {
    for (let y = 0; y < h; y++) {
        const row = y * w;
        let sum = 0, n = 0;
        for (let x = 0; x <= r && x < w; x++) { sum += a[row + x]; n++; }
        for (let x = 0; x < w; x++) {
            b[row + x] = sum / n;
            const add = x + r + 1, rem = x - r;
            if (add < w) { sum += a[row + add]; n++; }
            if (rem >= 0) { sum -= a[row + rem]; n--; }
        }
    }
}

function boxV(a, b, w, h, r) {
    for (let x = 0; x < w; x++) {
        let sum = 0, n = 0;
        for (let y = 0; y <= r && y < h; y++) { sum += a[y * w + x]; n++; }
        for (let y = 0; y < h; y++) {
            b[y * w + x] = sum / n;
            const add = y + r + 1, rem = y - r;
            if (add < h) { sum += a[add * w + x]; n++; }
            if (rem >= 0) { sum -= a[rem * w + x]; n--; }
        }
    }
}

/** Two box passes ≈ a Gaussian of σ ≈ r·0.8. r = 0 returns a float copy. */
export function blur(src, w, h, r) {
    const a = Float32Array.from(src);
    if (!(r > 0)) return a;
    const b = new Float32Array(w * h);
    for (let pass = 0; pass < 2; pass++) { boxH(a, b, w, h, r); boxV(b, a, w, h, r); }
    return a;
}

/**
 * Outline: blur → Sobel → thin to one pixel (non-maximum suppression) →
 * keep strong edges and the weak ones connected to them (hysteresis) → drop
 * short fragments (JPEG noise, skin texture). Returns a 0/255 mask.
 *
 * `detail` 0…100 moves three things together: less blur, a lower edge
 * threshold and a smaller minimum fragment. The threshold is taken from the
 * image's own gradient distribution, so a dim photo and a crisp scan give a
 * similar amount of line at the same setting — with an absolute floor, so a
 * flat image does not turn its noise into lines.
 */
export function outline(gray, w, h, opts) {
    const o = opts || {};
    const detail = Math.max(0, Math.min(100, o.detail == null ? 50 : o.detail)) / 100;
    const n = w * h;
    const out = new Uint8Array(n);
    if (w < 3 || h < 3) return out;
    const scale = Math.max(0.5, Math.min(3, Math.max(w, h) / 1000));
    const r = Math.max(1, Math.round((detail < 0.4 ? 2 : 1) * Math.max(1, scale * 0.8)));
    const g = blur(gray, w, h, r);

    const mag = new Float32Array(n);
    const dir = new Uint8Array(n);
    let maxM = 0;
    for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
            const i = y * w + x;
            const tl = g[i - w - 1], tc = g[i - w], tr = g[i - w + 1];
            const ml = g[i - 1], mr = g[i + 1];
            const bl = g[i + w - 1], bc = g[i + w], br = g[i + w + 1];
            const gx = (tr + 2 * mr + br) - (tl + 2 * ml + bl);
            const gy = (bl + 2 * bc + br) - (tl + 2 * tc + tr);
            const m = Math.sqrt(gx * gx + gy * gy);
            mag[i] = m;
            if (m > maxM) maxM = m;
            const ax = gx < 0 ? -gx : gx, ay = gy < 0 ? -gy : gy;
            dir[i] = ay <= ax * 0.41421356 ? 0 : ay >= ax * 2.41421356 ? 2 : (gx * gy > 0 ? 1 : 3);
        }
    }
    if (maxM <= 0) return out;

    // non-maximum suppression: an edge is the ridge of the gradient, 1 px wide
    const nms = new Float32Array(n);
    const offs = [1, w + 1, w, w - 1];
    for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
            const i = y * w + x, m = mag[i];
            if (m <= 0) continue;
            const d = offs[dir[i]];
            if (m >= mag[i + d] && m > mag[i - d]) nms[i] = m;
        }
    }

    // thresholds from the image's own ridge strengths
    const BINS = 2048;
    const hist = new Uint32Array(BINS);
    let ridges = 0;
    for (let i = 0; i < n; i++) {
        if (nms[i] > 0) { hist[Math.min(BINS - 1, (nms[i] / maxM * (BINS - 1)) | 0)]++; ridges++; }
    }
    const keepFrac = 0.04 + 0.30 * detail;                 // share of ridge pixels that are strong
    let target = ridges * keepFrac, acc = 0, bin = BINS - 1;
    for (; bin > 0; bin--) { acc += hist[bin]; if (acc >= target) break; }
    const floor = 60 - 45 * detail;                        // Sobel units: a 255 step is 1020
    const high = Math.max(floor, bin / (BINS - 1) * maxM);
    const low = high * 0.45;

    // hysteresis + fragment filter in one flood fill
    const minLen = Math.max(2, Math.round((26 - 22 * detail) * scale));
    const seen = new Uint8Array(n);
    const stack = new Int32Array(n);
    const comp = new Int32Array(n);
    for (let s = 0; s < n; s++) {
        if (seen[s] || nms[s] < high) continue;
        let sp = 0, cn = 0;
        stack[sp++] = s; seen[s] = 1;
        while (sp) {
            const i = stack[--sp];
            comp[cn++] = i;
            const x = i % w, y = (i - x) / w;
            for (let dy = -1; dy <= 1; dy++) {
                const yy = y + dy;
                if (yy < 0 || yy >= h) continue;
                for (let dx = -1; dx <= 1; dx++) {
                    const xx = x + dx;
                    if (xx < 0 || xx >= w || (!dx && !dy)) continue;
                    const j = yy * w + xx;
                    if (!seen[j] && nms[j] >= low) { seen[j] = 1; stack[sp++] = j; }
                }
            }
        }
        if (cn >= minLen) for (let k = 0; k < cn; k++) out[comp[k]] = 255;
    }
    return out;
}

/** Grow ink by `r` px in every direction (square kernel, separable max). */
export function dilate(mask, w, h, r) {
    if (!(r > 0)) return mask;
    const tmp = new Uint8Array(w * h), out = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) {
        const row = y * w;
        for (let x = 0; x < w; x++) {
            let v = 0;
            const x0 = Math.max(0, x - r), x1 = Math.min(w - 1, x + r);
            for (let k = x0; k <= x1; k++) if (mask[row + k]) { v = 255; break; }
            tmp[row + x] = v;
        }
    }
    for (let y = 0; y < h; y++) {
        const y0 = Math.max(0, y - r), y1 = Math.min(h - 1, y + r);
        for (let x = 0; x < w; x++) {
            let v = 0;
            for (let k = y0; k <= y1; k++) if (tmp[k * w + x]) { v = 255; break; }
            out[y * w + x] = v;
        }
    }
    return out;
}

/**
 * Line thickness asked in millimetres → dilation radius in working pixels.
 * `cssPerWorkPx` is how many screen CSS px one working pixel covers when the
 * picture is shown; a 1-px line is (1 + 2r) working px wide.
 */
export function dilationRadius(thickMm, pxPerMm, cssPerWorkPx) {
    if (!(cssPerWorkPx > 0) || !(pxPerMm > 0) || !(thickMm > 0)) return 0;
    const wantCss = thickMm * pxPerMm;
    return Math.max(0, Math.min(12, Math.round((wantCss / cssPerWorkPx - 1) / 2)));
}

/** Black ink with the mask as alpha — paper shows through everywhere else. */
export function maskToRgba(mask, out) {
    const o = out || new Uint8ClampedArray(mask.length * 4);
    for (let i = 0, j = 0; i < mask.length; i++, j += 4) {
        o[j] = 0; o[j + 1] = 0; o[j + 2] = 0; o[j + 3] = mask[i];
    }
    return o;
}

/**
 * The whole look on one RGBA buffer (w×h). Returns {rgba, level} — `level` is
 * the threshold actually used (Otsu when look.level is null).
 * look: {mode:'photo'|'outline'|'threshold', detail, level, gray, invert,
 *        contrast, brightness, dilate (radius, px)}
 */
export function processLook(rgba, w, h, look) {
    const n = w * h;
    if (look.mode === 'photo') {
        if (!isIdentityLook(look)) adjustRgba(rgba, n, look);
        return { rgba, level: null };
    }
    let gray = toGray(rgba, n);
    const lut = makeLut({ contrast: look.contrast, brightness: look.brightness, invert: look.mode === 'threshold' && look.invert });
    for (let i = 0; i < n; i++) gray[i] = lut[gray[i]];
    let mask, level = null;
    if (look.mode === 'threshold') {
        const soft = Math.max(w, h) > 700 ? blur(gray, w, h, 1) : gray;
        const g8 = soft instanceof Uint8ClampedArray ? soft : Uint8ClampedArray.from(soft);
        level = look.level == null ? otsu(g8) : look.level;
        mask = thresholdMask(g8, level);
    } else {
        mask = outline(gray, w, h, { detail: look.detail });
    }
    if (look.dilate > 0) mask = dilate(mask, w, h, look.dilate);
    return { rgba: maskToRgba(mask, rgba), level };
}

/* ── 4. print resolution stored in the file ───────────────────────────── */

function u16(b, i, le) { return le ? b[i] | (b[i + 1] << 8) : (b[i] << 8) | b[i + 1]; }
function u32(b, i, le) {
    return le ? (b[i] | (b[i + 1] << 8) | (b[i + 2] << 16) | (b[i + 3] << 24)) >>> 0
              : ((b[i] << 24) | (b[i + 1] << 16) | (b[i + 2] << 8) | b[i + 3]) >>> 0;
}

function exifDpi(b, start, len) {
    // "Exif\0\0" then a TIFF header
    if (len < 14 || b[start] !== 0x45 || b[start + 1] !== 0x78 || b[start + 2] !== 0x69 || b[start + 3] !== 0x66) return null;
    const t = start + 6;
    const le = b[t] === 0x49 && b[t + 1] === 0x49;
    if (!le && !(b[t] === 0x4d && b[t + 1] === 0x4d)) return null;
    const ifd = t + u32(b, t + 4, le);
    const end = start + len;
    if (ifd + 2 > end) return null;
    const cnt = u16(b, ifd, le);
    let xres = null, unit = 2;
    for (let k = 0; k < cnt; k++) {
        const e = ifd + 2 + k * 12;
        if (e + 12 > end) break;
        const tag = u16(b, e, le);
        if (tag === 0x011a) {
            const off = t + u32(b, e + 8, le);
            if (off + 8 <= end) {
                const num = u32(b, off, le), den = u32(b, off + 4, le);
                if (den) xres = num / den;
            }
        } else if (tag === 0x0128) {
            unit = u16(b, e + 8, le);
        }
    }
    if (!(xres > 0)) return null;
    if (unit === 3) return xres * 2.54;           // per centimetre
    if (unit === 2) return xres;
    return null;                                   // 1 = no absolute unit
}

/**
 * Dots per inch written into the file, or null. Reads the JPEG JFIF density,
 * the EXIF XResolution (preferred when both exist — scanners write it), or
 * the PNG pHYs chunk. Only the header bytes are needed (the first 64 KB).
 */
export function readDpi(bytes) {
    const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    if (b.length > 24 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) {
        let i = 8;
        while (i + 12 <= b.length) {
            const len = u32(b, i, false);
            const type = String.fromCharCode(b[i + 4], b[i + 5], b[i + 6], b[i + 7]);
            if (type === 'pHYs' && i + 8 + 9 <= b.length) {
                const ppu = u32(b, i + 8, false), unit = b[i + 16];
                if (unit === 1 && ppu > 0) return ppu * 0.0254;
                return null;
            }
            if (type === 'IDAT' || type === 'IEND') return null;
            i += 12 + len;
        }
        return null;
    }
    if (b.length > 4 && b[0] === 0xff && b[1] === 0xd8) {
        let i = 2, jfif = null, exif = null;
        while (i + 4 <= b.length) {
            if (b[i] !== 0xff) break;
            const m = b[i + 1];
            if (m === 0xd8 || (m >= 0xd0 && m <= 0xd7) || m === 0x01) { i += 2; continue; }
            if (m === 0xda || m === 0xd9) break;              // image data: headers are over
            const len = u16(b, i + 2, false);
            const s = i + 4;
            if (m === 0xe0 && len >= 14 && b[s] === 0x4a && b[s + 1] === 0x46 && b[s + 2] === 0x49 && b[s + 3] === 0x46 && b[s + 4] === 0) {
                const units = b[s + 7], xd = u16(b, s + 8, false);
                if (xd > 0 && units === 1) jfif = xd;
                else if (xd > 0 && units === 2) jfif = xd * 2.54;
            } else if (m === 0xe1) {
                const d = exifDpi(b, s, len - 2);
                if (d) exif = d;
            }
            i += 2 + len;
        }
        return exif || jfif || null;
    }
    return null;
}

/**
 * 72 and 96 dpi are what cameras and screenshots write when they know
 * nothing — «the size of the scan» is offered only for a real scan density.
 */
export function meaningfulDpi(dpi) {
    return dpi > 0 && dpi >= 100 && dpi <= 4800 ? Math.round(dpi) : null;
}
